import { Workbook } from 'exceljs';
import React, { useCallback, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '../../../../components/Providers/AnalyticsProvider';
import { StyledLoadingButton } from '../../../../theme/CustomComponents';
import { ProjectConfigQuestion } from '../../../../Types/dealGPT';
import { SourceDocument } from '../../../../Types/indexes';
import { downloadExcel } from '../../../../util/excel';

/**
 * Generates an Excel workbook from the rubric core data
 * @param {Record<string, Record<string, RubricCore[]>>} rubricCoreByArea - The rubric core data grouped by area and dimension
 * @returns {Workbook} The generated Excel workbook
 */
const generateExcel = async (data: Record<string, SourceDocument[]>): Promise<Workbook> => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet('Key Questions Summary');
  // Add the header row
  worksheet.addRow(['Key Question', 'File Name']);

  // Add the data rows
  Object.entries(data).forEach(([keyQuestion, documents]) => {
    if (documents.length === 0) {
      worksheet.addRow([
        keyQuestion,
        'No relevant documents found, have you ran a discovery scan yet?',
      ]);
    } else {
      documents.forEach((doc) => worksheet.addRow([keyQuestion, doc.fileName]));
    }
  });

  // Apply styles to the title row
  const titleRow = worksheet.getRow(1);
  titleRow.height = 30;
  titleRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center' };
    cell.font = { bold: true, size: 18 };
  });

  // Apply styles to the data rows
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber <= 1) return;
    row.eachCell((cell) => {
      cell.font = { size: 14 };
      cell.alignment = { wrapText: true };
    });
  });

  // Set minimum width for all columns
  worksheet.columns.forEach((column) => {
    column.width = 75;
  });

  return workbook;
};

interface DownloadKeyQuestionSummaryButtonProps {
  keyQuestions: ProjectConfigQuestion[];
  documentsMap: Record<string, SourceDocument[]>;
  runId: string;
  runScopes: string[];
}

export const DownloadKeyQuestionSummaryButton: React.FC<DownloadKeyQuestionSummaryButtonProps> = ({
  keyQuestions,
  documentsMap,
  runId,
  runScopes,
}) => {
  const [loading, setLoading] = useState(false);
  const analytics = useAnalytics();

  const onClick = useCallback(async () => {
    try {
      setLoading(true);
      analytics.event(
        AnalyticsEvent.WM_IA_D_KEY_QUESTIONS_DOWNLOAD,
        {
          id: keyQuestions[0].projectId,
        },
        {
          projectId: keyQuestions[0].projectId,
          runId,
          selectedScopes: runScopes,
        }
      );
      const questionDocumentsMap = keyQuestions.reduce<Record<string, SourceDocument[]>>(
        (acc, question) => {
          acc[question.question] = documentsMap[question.id as string];
          return acc;
        },
        {}
      );

      const excelContent = await generateExcel(questionDocumentsMap);
      downloadExcel(excelContent, 'Key Questions Summary.xlsx');
    } catch (error) {
      console.error('Error generating Key Questions Summary report:', error);
    } finally {
      setLoading(false);
    }
  }, [keyQuestions, documentsMap, analytics, runId, runScopes]);

  return (
    <StyledLoadingButton
      variant="outlined"
      loading={loading}
      loadingPosition="end"
      endIcon={<></>}
      onClick={onClick}
      disabled={Object.keys(documentsMap).length === 0}
    >
      Generate Report
    </StyledLoadingButton>
  );
};
